// Importação das funções auxiliares do serviço de autenticação
import {
    formatError,          // Formata mensagens de erro recebidas da API
    login,                // Função que realiza a autenticação do usuário via API
    runLogoutTimer,       // Configura um temporizador para logout automático quando o token expirar
    saveTokenInLocalStorage // Salva o token e os dados do usuário no armazenamento local
} from '../../services/AuthService';
import { clearAiSupportConversation } from '../../services/AiSupportStorage'; // Limpa a conversa do widget de ajuda

// Definição das constantes que representam os tipos de ações no Redux
export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login'; // Ação para login bem-sucedido
export const LOGIN_FAILED_ACTION = '[login action] failed login';       // Ação para login com falha
export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading'; // Ação para alternar estado de carregamento
export const LOGOUT_ACTION = '[Logout action] logout action';           // Ação para logout

/**
 * Função para realizar o logout do usuário.
 * Remove os dados do usuário do Local Storage e redireciona para a tela de login.
 *
 * @param {Function} navigate - Função para redirecionamento de páginas (React Router).
 * @returns {Object} - Ação de logout a ser processada pelo Redux.
 */
export function Logout(navigate) {
    localStorage.removeItem('userDetails'); // Remove os dados do usuário armazenados localmente

    // A conversa do widget de ajuda fica em sessionStorage, que pertence à aba e não à sessão
    // de login. Sem limpar aqui, quem entrasse em seguida no mesmo navegador leria as
    // perguntas do usuário anterior, que podem citar nome ou telefone de cliente.
    clearAiSupportConversation();

    if (navigate && typeof navigate === 'function') {
        navigate('/login'); // Redireciona o usuário para a tela de login
    }

    return {
        type: LOGOUT_ACTION, // Retorna a ação de logout para ser processada pelo Redux
    };
}

/**
 * Função para realizar o login do usuário.
 * Chama a API de autenticação e, em caso de sucesso, salva o token, configura o timer de logout e redireciona o usuário.
 *
 * @param {string} email - Email do usuário.
 * @param {string} password - Senha do usuário.
 * @param {Function} navigate - Função para redirecionamento de páginas (React Router).
 * @returns {Function} - Função de dispatch do Redux para processar a autenticação.
 */
export function loginAction(email, password, navigate) {
    return (dispatch) => {
        login(email, password) // Faz a requisição para autenticação
            .then((response) => {
                saveTokenInLocalStorage(response.data); // Salva os tokens e informações do usuário no Local Storage
                
                // Configura um temporizador para deslogar o usuário automaticamente após o token expirar
                runLogoutTimer(
                    dispatch,
                    response.data.expiresIn * 1000, // Multiplica por 1000 para converter segundos em milissegundos
                    navigate,
                );

                dispatch(loginConfirmedAction(response.data)); // Dispara a ação indicando login bem-sucedido
                navigate('/dashboard'); // Redireciona o usuário para o painel principal
            })
            .catch((error) => {
                const errorMessage = formatError(error.response.data); // Formata a mensagem de erro da API
                dispatch(loginFailedAction(errorMessage)); // Dispara ação indicando falha no login
            });
    };
}

/**
 * Cria uma ação para indicar falha no login.
 *
 * @param {string} data - Mensagem de erro recebida da API.
 * @returns {Object} - Ação a ser processada pelo Redux contendo a mensagem de erro.
 */
export function loginFailedAction(data) {
    return {
        type: LOGIN_FAILED_ACTION, // Tipo de ação para falha no login
        payload: data, // Mensagem de erro
    };
}

/**
 * Cria uma ação para indicar login bem-sucedido.
 *
 * @param {Object} data - Dados do usuário autenticado (token, email, etc.).
 * @returns {Object} - Ação a ser processada pelo Redux contendo os dados do usuário autenticado.
 */
export function loginConfirmedAction(data) {
    return {
        type: LOGIN_CONFIRMED_ACTION, // Tipo de ação para login bem-sucedido
        payload: data, // Dados do usuário autenticado
    };
}

/**
 * Cria uma ação para alternar o estado de carregamento da aplicação.
 *
 * @param {boolean} status - Indica se o carregamento está ativo (true) ou inativo (false).
 * @returns {Object} - Ação a ser processada pelo Redux para atualizar o estado de carregamento.
 */
export function loadingToggleAction(status) {
    return {
        type: LOADING_TOGGLE_ACTION, // Tipo de ação para alternar estado de carregamento
        payload: status, // Define o estado de carregamento como ativo ou inativo
    };
}
